import { ChangeEvent, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { url } from 'src/Helpers/utils/Constant';
import { RefreshButton } from 'src/Utils/CommonAllShowUtils';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import { debounce } from 'lodash';
import {
  ITraineeDraftApplicationListItem,
} from './types/traineeDraftApplicationTypes';
import TraineeDraftTableRow from './components/TraineeDraftTableRow';

interface IDraftApplicationState {
  applications: ITraineeDraftApplicationListItem[];
  total: number;
}

const TraineeDraftApplications = () => {
  const [draftState, setDraftState] = useState<IDraftApplicationState>({
    applications: [],
    total: 0,
  });
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(25);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [applicantName, setApplicantName] = useState<string>('');

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    let baseUrl = `${url}/api/training/course/application/draft/list?skip=${page * limit}&limit=${limit}`;

    if (applicantName.trim()) {
      baseUrl += `&applicant_name=${encodeURIComponent(applicantName.trim())}`;
    }

    setIsLoading(true);

    axios
      .get(baseUrl, { cancelToken: source.token })
      .then((res) => {
        if (res.data?.success) {
          setDraftState({
            applications: res.data.data || [],
            total: res.data.total || 0,
          });
        } else {
          setDraftState({ applications: [], total: 0 });
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error('Error fetching trainee draft applications:', err);
          setDraftState({ applications: [], total: 0 });
        }
        setIsLoading(false);
      });

    return () => {
      source.cancel();
    };
  }, [refresh, page, limit, applicantName]);

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (_event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleSearchChange = debounce((value: string) => {
    setApplicantName(value);
    setPage(0);
  }, 600);

  return (
    <>
      <Helmet>
        <title>Atab - Trainee Draft Applications</title>
      </Helmet>

      <Container maxWidth='xl' sx={{ py: 3 }}>
        <Grid
          container
          direction='row'
          justifyContent='center'
          alignItems='stretch'
          spacing={3}
        >
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='h4'>Trainee Draft Applications</Typography>
                    {draftState.total > 0 && (
                      <Typography variant='caption' color='text.secondary'>
                        ({draftState.total})
                      </Typography>
                    )}
                  </Box>
                }
                action={
                  <Box display='flex' alignItems='center' gap={1.5}>
                    <TextField
                      id='search-draft-applications'
                      label='Search by applicant name'
                      variant='outlined'
                      size='small'
                      placeholder='Type to search...'
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleSearchChange(e.target.value)
                      }
                      sx={{ minWidth: 260 }}
                    />
                    <RefreshButton action={() => setRefresh((prev) => !prev)} />
                  </Box>
                }
              />
              <Divider />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>SL</TableCell>
                      <TableCell>Applicant / Trainee</TableCell>
                      <TableCell>Course Name</TableCell>
                      <TableCell>Member Company</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align='center'>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableLoader />
                    ) : draftState.applications && draftState.applications.length > 0 ? (
                      draftState.applications.map((draftItem, index) => (
                        <TraineeDraftTableRow
                          key={draftItem.training_course_application_draft_id}
                          sl={page * limit + index + 1}
                          traineeDraftApplication={draftItem}
                        />
                      ))
                    ) : (
                      <TableNotFound />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box p={2}>
                <TablePagination
                  component='div'
                  count={draftState.total}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[25, 50, 75, 100, 400]}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default TraineeDraftApplications;
